import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Header } from "../Header";
import { useLocation } from "react-router-dom";
import './LedgerReport.css';
import { local_url, local_signage } from "../Const";
import Modal from "./modal";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const Ledger = () => {
  const location = useLocation();
  const { customerName: initialCustomerName, customerId: initialCustomerId } = location.state || {};

  const [customerName, setCustomerName] = useState(initialCustomerName || '');
  const [customerId, setCustomerId] = useState(initialCustomerId || '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());

  // New state variables for enhanced filtering
  const [segments, setSegments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [area, setArea] = useState('ALL');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchInputValue, setBranchInputValue] = useState('');

  // Get authentication token from localStorage
  const token = localStorage.getItem('AuthorizationToken');
  const userId = localStorage.getItem('userId');

  const handleFromDateChange = (newDate) => {
    setFromDate(newDate ? newDate.toDate() : new Date());
  };

  const handleToDateChange = (newDate) => {
    setToDate(newDate ? newDate.toDate() : new Date());
  };

  // Handle authentication error
  const handleAuthError = () => {
    setError("Authentication error. Please log in again.");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Advanced date formatter that handles various formats
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";

    // Handle DD-MM-YYYY format (already correct)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Handle D/M/YYYY or DD/MM/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const parts = dateString.split('/');
      // Ensure day and month are padded with leading zeros if needed
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${day}-${month}-${year}`;
    }

    // For any other format (ISO or JavaScript Date objects), convert to DD-MM-YYYY
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If date is invalid, return the original string
        return dateString;
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // Return original if parsing fails
    }
  };

  // Format date for API calls (YYYYMMDD format)
  const formatDate = (date) => {
    if (!date) return "";

    if (typeof date === 'string') {
      // If it's already a string, try to convert it to a Date first
      date = new Date(date);
    }

    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }

    return "";
  };

  // Fetch business segments when component mounts
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetch(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch business segments');
        }

        const data = await response.json();

        // Filter out deleted segments
        const activeSegments = data.filter(segment => segment.deleted === "N");
        setSegments(activeSegments);
      } catch (error) {
        console.error('Error fetching business segments:', error);
      }
    };

    if (token) {
      fetchSegments();
    }
  }, [token]);

  // Fetch areas based on selected branch type
  useEffect(() => {
    const fetchAreas = async () => {
      if (!branchType) return;

      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch areas by segment');
        }

        const data = await response.json();
        setAreas(data);
        // Reset area selection to ALL
        setArea("ALL");
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
      }
    };

    if (token && branchType) {
      fetchAreas();
    }
  }, [branchType, token]);

  // Fetch branches based on selected area and branch type
  const fetchBranchList = async (branchTypeValue, areaValue) => {
    if (!token) {
      console.error('Authentication token is missing');
      handleAuthError();
      return;
    }

    try {
      // Use the selected area ID for API call
      const areaParam = areaValue || "ALL";
      const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchTypeValue}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch branch list');
      }

      const data = await response.json();
      setBranchList(data);
    } catch (error) {
      console.error('Error fetching branch list:', error);
      setBranchList([]);
    }
  };

  // Update branches when area or branch type changes
  useEffect(() => {
    if (branchType) {
      fetchBranchList(branchType, area);
    }
  }, [area, branchType]);

  // Fetch customers based on selected area
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!area || !branchType) return;

      try {
        setLoading(true);
        const branchId = selectedBranch ? selectedBranch.branchID : "";
        const response = await fetch(
          `${local_url}/api/Customer/${branchId}/CS/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch customer data');
        }

        const data = await response.json();
        const formattedCustomers = data.branches?.map(branch => ({
          id: branch.customerCode,
          name: branch.customerName,
        })) || [];
        setCustomers(formattedCustomers);
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [area, branchType, selectedBranch, token]);

  // Fetch ledger data when customer or dates change
  useEffect(() => {
    if (customerId && fromDate && toDate && selectedBranch) {
      fetchLedgerData(fromDate, toDate);
    } else {
      // Clear ledger data if any required selections are missing
      setLedgerData([]);
    }
  }, [customerId, fromDate, toDate, selectedBranch]);

  const handleSelectCustomer = (id, name) => {
    setCustomerName(name);
    setCustomerId(id);
    setIsModalOpen(false);
  };

  const handleBranchTypeChange = (event) => {
    const newBranchType = event.target.value;
    setBranchType(newBranchType);
    setSelectedBranch(null);
    setBranchInputValue('');
    // Reset customer and ledger data when segment changes
    setCustomerName('');
    setCustomerId('');
    setLedgerData([]);
  };

  const handleAreaChange = (event) => {
    setArea(event.target.value);
    setSelectedBranch(null);
    setBranchInputValue('');
    // Reset customer and ledger data when area changes
    setCustomerName('');
    setCustomerId('');
    setLedgerData([]);
  };

  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
    // Reset customer and ledger data when branch changes
    setCustomerName('');
    setCustomerId('');
    setLedgerData([]);
  };

  const fetchLedgerData = async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const formattedFromDate = formatDate(from);
      const formattedToDate = formatDate(to);
      const areaToUse = area !== 'ALL' ? area : (selectedBranch?.area || 'L111');

      const response = await fetch(
        `${local_url}/api/Ledger/Get/${selectedBranch.branchID}/${customerId}/${formattedFromDate}/${formattedToDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      // Process the data and sort by businessDate
      const processedData = data.map(item => ({
        ...item,
        formattedDate: formatDisplayDate(item.businessDate)
      }));

      // Sort by date (assuming businessDate can be parsed correctly)
      const sortedData = processedData.sort((a, b) => {
        const dateA = parseDateForSorting(a.businessDate);
        const dateB = parseDateForSorting(b.businessDate);
        return dateA - dateB;
      });

      // Calculate cumulative balances
      const cumulativeBalances = sortedData.reduce((acc, item) => {
        const receivable = item.receivable || 0;
        const receipt = item.receipt || 0;
        const balance = receivable - receipt;
        const lastCumulativeBalance = acc.length > 0 ? acc[acc.length - 1].cumulativeBalance : 0;
        const cumulativeBalance = lastCumulativeBalance + balance;
        acc.push({ ...item, balance, cumulativeBalance });
        return acc;
      }, []);

      setLedgerData(cumulativeBalances);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse dates for sorting
  const parseDateForSorting = (dateString) => {
    if (!dateString) return new Date(0);

    // Handle DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    // Handle D/M/YYYY or DD/MM/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/').map(Number);
      return new Date(year, month - 1, day);
    }

    // For any other format, try direct parsing
    return new Date(dateString);
  };

  const exportToCSV = () => {
    if (ledgerData.length === 0) return;

    const csvRows = [];
    const headers = ["Doc No", "CN Issue", "Date", "Trans Type ID", "Receivable", "Receipt", "Balance"];
    csvRows.push(headers.join(','));

    // Add customer name, code and selected date range as metadata
    csvRows.push(`Customer Name: ${customerName}`);
    csvRows.push(`Customer Code: ${customerId}`);
    csvRows.push(`Date Range: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`);
    csvRows.push(" "); // Empty row for spacing

    // Process each ledger data item
    ledgerData.forEach(item => {
      // Format the date to DD-MM-YYYY
      let formattedDate = item.businessDate;

      // Handle D/M/YYYY format
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(item.businessDate)) {
        const [day, month, year] = item.businessDate.split('/');
        formattedDate = `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
      }
      // If already in DD-MM-YYYY format, leave as is

      const row = [
        item.isO_Number || '',
        (item.cNissued || 0).toFixed(2),
        formattedDate,
        item.transTypeID || '',
        (item.receivable || 0).toFixed(2),
        (item.receipt || 0).toFixed(2),
        (item.cumulativeBalance || 0).toFixed(2) + (item.cumulativeBalance < 0 ? ' (Dr)' : ' (Cr)')
      ];
      csvRows.push(row.join(','));
    });

    // Add total row
    const totalReceivable = ledgerData.reduce((sum, item) => sum + (item.receivable || 0), 0).toFixed(2);
    const totalReceipt = ledgerData.reduce((sum, item) => sum + (item.receipt || 0), 0).toFixed(2);
    const finalBalance = ledgerData.length > 0 ?
      (Math.abs(ledgerData[ledgerData.length - 1].cumulativeBalance)).toFixed(2) +
      (ledgerData[ledgerData.length - 1].cumulativeBalance < 0 ? ' (Dr)' : ' (Cr)') :
      '0.00';

    csvRows.push(["Total", "", "", "", totalReceivable, totalReceipt, finalBalance].join(','));

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    // Format dates in filename using DD-MM-YYYY format
    const fromDateFormatted = formatDateForFilename(fromDate);
    const toDateFormatted = formatDateForFilename(toDate);

    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `ledger_${customerName}_${fromDateFormatted}_to_${toDateFormatted}.csv`);
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper function to format date specifically for filenames
  const formatDateForFilename = (date) => {
    if (!date) return "";

    if (typeof date === 'string') {
      // If D/M/YYYY format
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
      }
      // If already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
        return date;
      }
      // Otherwise try to convert from Date object
      date = new Date(date);
    }

    if (date instanceof Date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    return "";
  };

  // Enhanced Loader component with pulse animation
  const Loader = () => (
    <div className="Ledger-loader-container">
      <div className="Ledger-loader-pulse"></div>
      <p className="Ledger-loader-text">Loading data...</p>
    </div>
  );

  return (
    <>
      <Header />
      <div className="Ledger-container">
        {loading && <Loader />}

        <div className="Ledger-customer-input">
          {/* Segment Dropdown */}
          <FormControl variant="outlined" className="Ledger-formControl" style={{ minWidth: 150 }}>
            <InputLabel id="branch-type-select-label">Segment</InputLabel>
            <Select
              labelId="branch-type-select-label"
              value={branchType}
              onChange={handleBranchTypeChange}
              label="Segment"
              disabled={loading}
            >
              {segments.map((segment) => (
                <MenuItem key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                  {segment.businessSegmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Area Dropdown */}
          <FormControl variant="outlined" className="Ledger-formControl" style={{ minWidth: 130, maxWidth: 130 }}>
            <InputLabel id="area-select-label">Area</InputLabel>
            <Select
              labelId="area-select-label"
              value={area}
              onChange={handleAreaChange}
              label="Area"
              disabled={loading || !branchType}
            >
              <MenuItem value="ALL">All</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                  {area.SFAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Branch Autocomplete */}
          {branchType && (
            <Autocomplete
              style={{ minWidth: 180, maxWidth: 180 }}
              options={branchList || []}
              getOptionLabel={(option) => option.branchName ? `${option.branchName}(${option.branchID})` : ""}
              value={selectedBranch}
              onChange={handleBranchChange}
              inputValue={branchInputValue}
              onInputChange={(event, newInputValue) => {
                setBranchInputValue(newInputValue);
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Branch"
                  variant="outlined"
                  size="medium"
                />
              )}
              isOptionEqualToValue={(option, value) => option.branchID === value?.branchID}
            />
          )}

          <label className="Ledger-label">
            <input
              type="text"
              disabled={true}
              onClick={() => !loading && branchType && setIsModalOpen(true)}
              value={customerName}
              placeholder="Select customer"
              className="Ledger-customer-field"
              readOnly
            />
          </label>
          <button
            onClick={() => setIsModalOpen(true)}
            disabled={loading || !branchType || !selectedBranch}
            className="Ledger-button"
          >
            Select Customer
          </button>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={dayjs(fromDate)}
              onChange={handleFromDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={dayjs(toDate)}
              onChange={handleToDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>
          <button
            onClick={exportToCSV}
            className="Ledger-button Ledger-export-button"
            disabled={loading || ledgerData.length === 0}
          >
            Export to CSV
          </button>
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelect={handleSelectCustomer}
          customers={customers}
          className="Ledger-modal"
          contentClass="Ledger-modal-content"
          overlayClass="Ledger-modal-overlay"
        />

        {error && <div className="Ledger-error-message">{error}</div>}

        <div className="Ledger-table-container">
          <table className="Ledger-customer">
            <thead>
              <tr>
                <th>Doc No</th>
                <th>CN Issue</th>
                <th>Date</th>
                <th>Trans Type ID</th>
                <th>Receivable</th>
                <th>Receipt</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledgerData.length > 0 ? (
                ledgerData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.isO_Number}</td>
                    <td>₹{item.cNissued?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                    <td>{formatDisplayDate(item.businessDate)}</td>
                    <td>{item.transTypeID}</td>
                    <td className="Ledger-currency-positive">₹{item.receivable?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                    <td className="Ledger-currency-negative">₹{item.receipt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                    <td className={item.cumulativeBalance >= 0 ? "Ledger-currency-positive" : "Ledger-currency-negative"}>
                      ₹{Math.abs(item.cumulativeBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {/* {item.cumulativeBalance < 0 ? ' (Dr)' : ' (Cr)'} */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="Ledger-no-data">
                    {customerId ? "No ledger data available for the selected period." : "Please select a customer to view ledger data."}
                  </td>
                </tr>
              )}
            </tbody>
            {ledgerData.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                  <td className="Ledger-currency-positive">
                    ₹{ledgerData.reduce((sum, item) => sum + (item.receivable || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="Ledger-currency-negative">
                    ₹{ledgerData.reduce((sum, item) => sum + (item.receipt || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={ledgerData[ledgerData.length - 1]?.cumulativeBalance >= 0 ?
                    "Ledger-currency-positive" : "Ledger-currency-negative"}>
                    ₹{Math.abs(ledgerData[ledgerData.length - 1]?.cumulativeBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {/* {ledgerData[ledgerData.length-1]?.cumulativeBalance < 0 ? ' (Dr)' : ' (Cr)'} */}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
};

Ledger.propTypes = {
  customerName: PropTypes.string,
  customerId: PropTypes.string,
};

export default Ledger;